// src/pages/fixed-assets/FixedAssetEdit.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { apiGet, apiPatch } from "../../api/client";
import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { AppleDateInput } from "../../components/AppleDateInput";
import { companyRoute } from "../../utils/routeHelpers";
import { mdyToYmd, formatMMDDYYYYFromYMD } from "../../utils/dateUtils";
import { A } from "../../styles/appleClasses";
import { isClosedPeriodError } from "../../utils/closedPeriod";

type FixedAsset = {
  id: number;
  name: string;

  asset_account_id?: number | null;
  asset_account_name?: string | null;

  disposed?: boolean;
  memo?: string | null;
};

type CategoryOption = {
  id: number;
  name: string;
};

type DepScheduleDetail = {
  id: number;
  asset_id: number;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD (computed by backend in edit-payload)
  useful_life_months: number;
  status: string; // active | closed | voided
};

type EditPayload = {
  asset: FixedAsset;
  categories: CategoryOption[];
  schedule: DepScheduleDetail | null;
};

type LocalErrorKey =
  | "fixedAssetEdit.errors.notFound"
  | "fixedAssetEdit.errors.load"
  | "fixedAssetEdit.errors.nameRequired"
  | "fixedAssetEdit.errors.endDateRequired"
  | "fixedAssetEdit.errors.endDateInvalid"
  | "fixedAssetEdit.errors.endBeforePurchaseMonth"
  | "fixedAssetEdit.errors.validCategoryRequired"
  | "fixedAssetEdit.errors.save"
  | "expenses.errors.selectCategory"
  | "sales.errors.closedEdit";

type PageError =
  | { kind: "key"; key: LocalErrorKey }
  | { kind: "raw"; value: any };

function apiPageError(error: any, fallback: LocalErrorKey): PageError {
  // Preserve the existing detail/message precedence without inferring provenance from text.
  const raw = error?.response?.data?.detail || error?.message;
  return raw ? { kind: "raw", value: raw } : { kind: "key", key: fallback };
}

type TextEl = HTMLInputElement | HTMLTextAreaElement;
function selectAllOnFocus<T extends TextEl>(
  e: React.FocusEvent<T> | React.MouseEvent<T>,
) {
  const el = e.currentTarget;
  setTimeout(() => el.select(), 0);
}
function focusAndSelectOnMouseDown<T extends TextEl>(e: React.MouseEvent<T>) {
  const el = e.currentTarget;
  if (document.activeElement !== el) {
    e.preventDefault();
    el.focus();
    el.select();
  }
}

// Inclusive month count by (YYYY-MM) boundaries
function lifeMonthsFromStartAndEnd(
  startYmd: string,
  endYmd: string,
): number | null {
  const a = String(startYmd || "").match(/^(\d{4})-(\d{2})-/);
  const b = String(endYmd || "").match(/^(\d{4})-(\d{2})-/);
  if (!a || !b) return null;

  const sy = Number(a[1]);
  const sm = Number(a[2]);
  const ey = Number(b[1]);
  const em = Number(b[2]);

  if (
    !Number.isFinite(sy) ||
    !Number.isFinite(sm) ||
    !Number.isFinite(ey) ||
    !Number.isFinite(em)
  )
    return null;

  const diff = (ey - sy) * 12 + (em - sm) + 1;
  if (diff <= 0) return null;
  return diff;
}

export default function FixedAssetEdit() {
  const { t } = useTranslation();
  const { company_id = "", asset_id = "" } = useParams<{
    company_id: string;
    asset_id: string;
  }>();

  const navigate = useNavigate();
  const go = (path: string) => navigate(companyRoute(company_id, path));
  const backToDetail = () => go(`/fixed-assets/${asset_id}`);

  const withCompany = (path: string) => {
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}company_id=${company_id}`;
  };

  const getC = async <T,>(path: string): Promise<T> => {
    if (!company_id) return null as unknown as T;
    return apiGet<T>(withCompany(path));
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<PageError | null>(null);

  const [name, setName] = useState("");
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [originalAssetAccountId, setOriginalAssetAccountId] = useState<
    number | null
  >(null);
  const [sched, setSched] = useState<DepScheduleDetail | null>(null);

  // ✅ Use the same AppleDateInput pattern as ExpenseEntry:
  // store YMD for API, input renders MM/DD/YYYY with calendar icon
  const [endYmd, setEndYmd] = useState<string>(""); // YYYY-MM-DD

  const computedLifeMonths = useMemo(() => {
    if (!sched?.start_date) return null;
    if (!endYmd) return null;
    return lifeMonthsFromStartAndEnd(sched.start_date, endYmd);
  }, [sched?.start_date, endYmd]);

  const categoryChanged = useMemo(() => {
    if (!category?.id || originalAssetAccountId == null) return false;
    return Number(category.id) !== Number(originalAssetAccountId);
  }, [category?.id, originalAssetAccountId]);

  useEffect(() => {
    if (!company_id || !asset_id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr(null);

      try {
        const p = await getC<EditPayload>(
          `/fixed-assets/${asset_id}/edit-payload`,
        );
        if (cancelled) return;

        if (!p?.asset) {
          setErr({ kind: "key", key: "fixedAssetEdit.errors.notFound" });
          return;
        }

        const a = p.asset;
        const list = Array.isArray(p.categories) ? p.categories : [];
        const s = p.schedule ?? null;

        setOriginalAssetAccountId(a.asset_account_id ?? null);

        setName(a.name || "");
        setCategories(list);

        const matched =
          (a.asset_account_id != null
            ? list.find((x) => Number(x.id) === Number(a.asset_account_id))
            : null) ||
          (a.asset_account_name
            ? list.find(
                (x) =>
                  (x.name || "").toLowerCase().trim() ===
                  (a.asset_account_name || "").toLowerCase().trim(),
              )
            : null) ||
          list[0] ||
          null;

        setCategory(matched);

        if (s?.id) {
          setSched(s);
          setEndYmd(s.end_date || "");
        } else {
          setSched(null);
          setEndYmd("");
        }
      } catch (e: any) {
        if (cancelled) return;
        setErr(
          apiPageError(e, "fixedAssetEdit.errors.load"),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id, asset_id]);

  const save = async () => {
    if (!company_id || !asset_id) return;
    setErr(null);

    const nm = (name || "").trim();
    if (!nm) return setErr({ kind: "key", key: "fixedAssetEdit.errors.nameRequired" });
    if (!category?.id) return setErr({ kind: "key", key: "expenses.errors.selectCategory" });

    let newLife: number | null = null;
    if (sched?.id) {
      if (!endYmd) return setErr({ kind: "key", key: "fixedAssetEdit.errors.endDateRequired" });

      // defensive: ensure it is date-only
      // (AppleDateInput should already give YYYY-MM-DD)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(endYmd)) {
        return setErr({ kind: "key", key: "fixedAssetEdit.errors.endDateInvalid" });
      }

      newLife = lifeMonthsFromStartAndEnd(sched.start_date, endYmd);
      if (!newLife)
        return setErr({ kind: "key", key: "fixedAssetEdit.errors.endBeforePurchaseMonth" });
    }

    setSaving(true);
    try {
      const payload: any = {
        name: nm,
      };

      if (categoryChanged) {
        const nextAccountId = Number(category.id);

        if (!Number.isFinite(nextAccountId)) {
          return setErr({ kind: "key", key: "fixedAssetEdit.errors.validCategoryRequired" });
        }

        payload.asset_account_id = nextAccountId;
      }

      await apiPatch(withCompany(`/fixed-assets/${asset_id}`), payload);

      if (
        sched?.id &&
        newLife &&
        Number.isFinite(newLife) &&
        newLife !== sched.useful_life_months
      ) {
        await apiPatch(
          withCompany(`/fixed-assets/schedules/${sched.id}/revise`),
          {
            new_end_date: endYmd,
          },
        );
      }

      backToDetail();
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setErr({ kind: "key", key: "sales.errors.closedEdit" });
        return;
      }

      setErr(apiPageError(e, "fixedAssetEdit.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  const labelCls = `block mb-1 ${A.label}`;
  const iconBtnCls =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  return (
    <div
      className={`min-h-[100dvh] bg-gray-50 flex flex-col items-center px-2 py-6 ${A.body}`}
    >
      {/* Header (sticky spacing tokens: px-2, mb-2) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={backToDetail}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
          title={t("directory.common.back")}
          disabled={saving}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none">
          <div className={A.title}>{t("fixedAssetEdit.title")}</div>
        </div>

        <button
          type="button"
          onClick={backToDetail}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
          title={t("directory.common.close")}
          disabled={saving}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <div className={`${A.card} w-full max-w-3xl p-5 sm:p-6`}>
        {loading ? (
          <div className="text-center py-6 text-gray-500 text-[13px]">
            {t("directory.common.loading")}
          </div>
        ) : null}

        {err ? (
          <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {err.kind === "key" ? t(err.key) : err.value}
          </div>
        ) : (
          <div style={{ minHeight: 18 }} className="mb-3" />
        )}

        <div className="grid grid-cols-1 gap-4">
          {/* Name */}
          <div>
            <label className={labelCls}>{t("fixedAssetEdit.assetName")}</label>
            <input
              className={A.inputBase}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={selectAllOnFocus}
              onMouseDown={focusAndSelectOnMouseDown}
              placeholder={t("fixedAssetEdit.assetNamePlaceholder")}
              disabled={loading || saving}
            />
          </div>

          {/* Category */}
          <div>
            <AppleComboBox<CategoryOption>
              label={t("expenses.common.category")}
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              options={categories}
              value={category}
              onChange={setCategory}
              getOptionLabel={(k) => k?.name || ""}
              placeholder={
                categories.length ? t("fixedAssetEdit.selectCategory") : t("expenses.categories.noCategories")
              }
              disabled={loading || saving}
              inputProps={{ className: A.inputBase }}
            />
            {!categories.length ? (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 border border-amber-100">
                {t("fixedAssetEdit.noPresetCategories")}
              </div>
            ) : null}
          </div>

          {/* Depreciation period */}
          {sched ? (
            <div className={`${A.card} px-4 py-4`}>
              <div className={`${A.title} mb-3`}>{t("fixedAssetEdit.depreciationPeriod")}</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t("fixedAssetEdit.purchaseDate")}</label>
                  <input
                    className={`${A.inputBase} bg-gray-50 tabular-nums`}
                    value={formatMMDDYYYYFromYMD(sched.start_date)}
                    disabled
                  />
                </div>

                <div>
                  <label className={labelCls}>{t("fixedAssetEdit.endDate")}</label>

                  {/* ✅ AppleDateInput (same as ExpenseEntry) */}
                  <AppleDateInput
                    valueYmd={endYmd}
                    onChangeYmd={setEndYmd}
                    allowEmpty={false}
                    ariaLabel={t("fixedAssetEdit.endDate")}
                    openCalendarLabel={t("sales.shared.openCalendar")}
                    className={A.num}
                    disabled={loading || saving}
                  />

                  {computedLifeMonths ? (
                    <div className="mt-1 text-xs text-gray-500">
                      {t("fixedAssetEdit.usefulLife")}{" "}
                      <span className="tabular-nums">{computedLifeMonths}</span>{" "}
                      {t("fixedAssetEdit.months")}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="text-xs text-gray-400 mt-3">
                {t("fixedAssetEdit.prospectiveChanges")}
              </div>
            </div>
          ) : (
            <div className="text-[13px] text-gray-500">
              {t("fixedAssetEdit.noSchedule")}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={backToDetail}
              disabled={saving}
            >
              {t("directory.common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={save}
              disabled={saving || loading}
            >
              {saving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
